import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "../../../firebase.js";
import { collection, getDocs } from "firebase/firestore";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Alert,
  Chip,
  Divider,
  Stack,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { de } from "date-fns/locale";

const IntegrationsStatus = () => {
  const { companyID, integrationId } = useParams();
  const [integracion, setIntegracion] = useState(null);
  const [instancias, setInstancias] = useState([]);

  useEffect(() => {
    getIntegrations(companyID);
  }, [integrationId, companyID]);

  const getIntegrations = async (companyID) => {
    try {
      const tiposSnap = await getDocs(collection(db, "xpress_integrations"));
      const tiposDisponibles = tiposSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      for (let index = 0; index < tiposDisponibles.length; index++) {
        if (tiposDisponibles[index].id === integrationId) {
          console.log("Integración en curso:", tiposDisponibles[index].id);
        } else {
          console.log(tiposDisponibles[index].id);
        }
      }

      const empresaRef = collection(
        db,
        "xpress_companies",
        companyID,
        "integrations"
      );
      const instanciasSnap = await getDocs(empresaRef);

      const instancias = instanciasSnap.empty
        ? []
        : instanciasSnap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
      setInstancias(instancias);

      const tipo = tiposDisponibles.find(
        (t) => t.integrationID === integrationId
      );

      if (!tipo || !tipo.enabled) {
        setIntegracion("not_found");
        return;
      }

      const instanciasDelTipo = instancias.filter(
        (i) => i.integrationID === tipo.integrationID
      );

      let status = "inactive";
      if (instanciasDelTipo.length > 0) {
        const connected = instanciasDelTipo.find(
          (i) => i.status === "connected"
        );
        status = connected
          ? "connected"
          : instanciasDelTipo[0].status || "unknown";
      }

      setIntegracion({
        ...tipo,
        instancias: instanciasDelTipo,
        status,
      });
    } catch (error) {
      console.error("❌ Error fetching integrations:", error);
    }
  };

  const handleConnect = (integracion) => {
    console.log(
      "🔗 Lógica para crear instancia del servicio...",
      integracion.id
    );
    switch (integracion.category) {
      case "logistics":
        createInstanciaLogistica(integracion);
        return;
      case "ecommerce":
        createInstanciaWoocomerce(integracion);
        return;
      case "integrations":
        createInstanciaIntegracion(integracion);
        return;
      case "marketplace":
        createInstanciaMercadolibre(integracion);
        return;
      default:
        return;
    }
  };

  const categoryLabels = {
    logistics: "Activar módulo logístico",
    ecommerce: "Conectar canal eCommerce",
    integrations: "Configurar API de Integraciones",
    marketplace: "Vincular Marketplace",
  };

  async function createInstanciaLogistica(integracion) {
    console.log(
      "🔗 Lógica para crear instancia del servicio bicci...",
      integracion
    );
  }
  async function createInstanciaWoocomerce(integracion) {
    console.log(
      "🔗 Lógica para crear instancia del Woocommerce...",
      integracion
    );
  }
  async function createInstanciaIntegracion(integracion) {
    console.log("🔗 Lógica para crear instancia de integrations", integracion);
  }
  async function createInstanciaMercadolibre(integracion) {
    console.log("🔗 Lógica para crear instancia de marketplace", integracion);
  }

  if (integracion === "not_found") {
    return (
      <Box p={2}>
        <Alert severity="error">
          Este servicio no existe o no está habilitado actualmente.
        </Alert>
      </Box>
    );
  }

  if (!integracion) return null;

  const isIntegrationEnabled = integracion.enabled; // si el tipo está habilitado
  const isConnected = integracion.status === "connected";
  const hasInstancia = integracion.instancias?.length > 0;
  const isInactive = integracion.status === "inactive";

  return (
    <Box p={2}>
      <Card sx={{ maxWidth: 600, margin: "0 auto" }}>
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="h5">{integracion.name}</Typography>
            <Chip
              label={`Estado del Servicio: ${
                isIntegrationEnabled ? "Habilitado" : "Deshabilitado"
              }`}
              color={
                isIntegrationEnabled
                  ? "success"
                  : isInactive
                  ? "default"
                  : "warning"
              }
              icon={
                isIntegrationEnabled ? (
                  <CheckCircleIcon />
                ) : isInactive ? (
                  <ErrorOutlineIcon />
                ) : (
                  <WarningAmberIcon />
                )
              }
              sx={{ width: "fit-content" }}
            />

            <Typography variant="body2" color="text.secondary">
              {integracion.description}
            </Typography>

            <Chip
              label={`Estado: ${integracion.status}`}
              color={
                isConnected ? "success" : isInactive ? "default" : "warning"
              }
              icon={
                isConnected ? (
                  <CheckCircleIcon />
                ) : isInactive ? (
                  <ErrorOutlineIcon />
                ) : (
                  <WarningAmberIcon />
                )
              }
              sx={{ width: "fit-content" }}
            />

            <Divider />

            <Typography variant="body2" color="text.secondary">
              Documentación:{" "}
              <a
                href={integracion.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Ver documentación
              </a>
            </Typography>
          </Stack>
        </CardContent>
        <CardActions>
          {!isIntegrationEnabled ? (
            <Chip label="En mantenimiento" color="default" />
          ) : isConnected ? (
            <Chip label="Conectado" color="success" />
          ) : hasInstancia ? (
            <Button
              onClick={() => handleConnect(integracion)}
              variant="contained"
              color="warning"
            >
              Reconectar
            </Button>
          ) : (
            <Button
              onClick={() => handleConnect(integracion)}
              variant="contained"
              color="primary"
            >
              {categoryLabels[integracion.category] || "Conectar"}
            </Button>
          )}
        </CardActions>
      </Card>
    </Box>
  );
};

export default IntegrationsStatus;
